// src/pages/intentions/SubmitIntentions.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Clock, CheckCircle, AlertCircle, Send, Save, Calendar, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { intentionPeriodsService } from '../../services/intentionPeriodsService';
import { intentionsService } from '../../services/intentionsService';
import { IntentionPeriod, AvailabilityData, PreferencesData, EmploymentDetailsData } from '../../types/intentionPeriods';
import { TeachingIntention } from '../../types/intentions';
import AvailabilitySection from '../../components/intentions/AvailabilitySection';
import PreferencesSection from '../../components/intentions/PreferencesSection';
import RUAHPreferencesSection from '../../components/intentions/RUAHPreferencesSection';
import EmploymentDetailsSection from '../../components/intentions/EmploymentDetailsSection';
import TILActivitiesSection from '../../components/intentions/TILActivitiesSection';
import Alert from '../../components/ui/Alert';
// Using crypto.randomUUID for UUID generation (browser native)

const SubmitIntentions: React.FC = () => {
  const { user } = useAuth();
  // Identifier written to teaching_intentions.staff_id. This column holds the
  // staff_code (the grid/allocation key the downstream matcher joins on), so we
  // prefer staffCode. The ~25 staff with no staff_code fall back to staffId
  // (staff.staff_id) — the own-row RLS policy accepts either.
  const staffIdentifier = user?.staffCode || user?.staffId || null;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentPeriod, setCurrentPeriod] = useState<IntentionPeriod | null>(null);
  const [existingIntention, setExistingIntention] = useState<TeachingIntention | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  // All outstanding validation problems, shown together so the user fixes them in one pass.
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  // Autosave / unsaved-changes tracking.
  const [dirty, setDirty] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const formLoadedRef = useRef(false);

  // Form state
  const [availability, setAvailability] = useState<AvailabilityData>({
    fte: 1.0,
    ftePreference: undefined,
    dayOffPreference: undefined,
    leavePlans: ''
  });

  const [preferences, setPreferences] = useState<PreferencesData>({
    curriculumAreas: [],
    subjects: [],
    yearLevels: []
  });

  const [employmentDetails, setEmploymentDetails] = useState<EmploymentDetailsData>({
    employmentType: 'ongoing',
    intentionToReturn: null,
    wishToBeConsidered: null,
    notReturningReason: '',
    hasLeavePlans: false,
    leaveType: null,
    leaveStartDate: undefined,
    leaveEndDate: undefined,
    year9StretchInterest: null,
    tilActivities: []
  });

  const [comments, setComments] = useState('');

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Always load the period first so the screen reflects reality. A missing
      // staff identifier must NOT masquerade as "no open period" — that gap is
      // surfaced separately below once we know a period exists.
      const period = await intentionPeriodsService.getCurrentOpenPeriod();
      setCurrentPeriod(period);

      if (period && staffIdentifier) {
        // Check for existing submission
        const existing = await intentionsService.getStaffIntentionForPeriod(
          staffIdentifier,
          period.id
        );

        if (existing) {
          setExistingIntention(existing);

          // Populate form with existing data
          if (existing.availability) {
            setAvailability(existing.availability);
          }
          if (existing.preferences) {
            setPreferences(existing.preferences);
          }
          if (existing.employmentDetails) {
            setEmploymentDetails(existing.employmentDetails);
          }
          if (existing.additionalComments) {
            setComments(existing.additionalComments);
          }
        }
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load submission data');
    } finally {
      setLoading(false);
    }
  };

  // Build the draft intention from the current form state (shared by the manual
  // "Save draft" button and the background autosave).
  const buildDraftIntention = (): TeachingIntention => {
    const derivedIntentionToReturn = employmentDetails.employmentType === 'ongoing'
      ? employmentDetails.intentionToReturn || 'uncertain'
      : employmentDetails.wishToBeConsidered || 'uncertain';
    return {
      id: existingIntention?.id || crypto.randomUUID(),
      staffId: staffIdentifier!,
      staffName: user?.name || '',
      email: user?.email,
      department: user?.department || '',
      year: currentPeriod!.academicYear,
      periodId: currentPeriod!.id,
      status: 'draft',
      availability,
      preferences,
      employmentDetails,
      additionalComments: comments,
      submittedAt: new Date(),
      lastUpdatedAt: new Date(),
      currentEmploymentType: employmentDetails.employmentType === 'ongoing' ? 'full-time' : 'fixed-term',
      currentFTE: availability.fte,
      intentionToReturn: derivedIntentionToReturn,
      desiredEmploymentStatus: derivedIntentionToReturn === 'yes' ? 'full-time' : 'not-returning',
      targetFTE: availability.fte,
      teachingPreferences: [],
      comfortableTeachingAreas: preferences.curriculumAreas.map(a => a.areaName)
    };
  };

  const handleSaveDraft = async () => {
    if (!user || !staffIdentifier || !currentPeriod) return;

    try {
      setSaving(true);
      setError(null);

      const intention = buildDraftIntention();
      await intentionsService.saveIntentionForPeriod(intention, currentPeriod.id);

      setSuccessMessage('Draft saved successfully');
      setExistingIntention(intention);
      setDirty(false);
      setLastSavedAt(new Date());

      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error saving draft:', err);
      setError('Failed to save draft');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!user || !staffIdentifier || !currentPeriod) return;

    // Collect ALL validation problems so the user can fix them in one pass,
    // rather than one error at a time. Rules are unchanged.
    const errs: string[] = [];

    const currentIntention = employmentDetails.employmentType === 'ongoing'
      ? employmentDetails.intentionToReturn
      : employmentDetails.wishToBeConsidered;

    if (!currentIntention) {
      errs.push(employmentDetails.employmentType === 'ongoing'
        ? 'Indicate whether you intend to return next year'
        : 'Indicate whether you wish to be considered for employment');
    }

    if (employmentDetails.hasLeavePlans) {
      if (!employmentDetails.leaveType) {
        errs.push('Select a leave type');
      }
      if (!employmentDetails.leaveStartDate || !employmentDetails.leaveEndDate) {
        errs.push('Provide both leave start and end dates');
      } else if (employmentDetails.leaveEndDate < employmentDetails.leaveStartDate) {
        errs.push('Leave end date must be after the start date');
      }
    }

    const requiresDetailedInfo = currentIntention === 'yes' || currentIntention === 'uncertain';

    if (requiresDetailedInfo) {
      if (employmentDetails.tilActivities.length < 5) {
        errs.push('Select at least 5 TIL activities');
      }
      if (employmentDetails.year9StretchInterest === null) {
        errs.push('Indicate your interest in the Year 9 Stretch Program');
      }
      if (!availability.ftePreference) {
        errs.push('Select your preferred load (1.0, 0.8, 0.75, 0.6, 0.5, 0.4, or "discuss")');
      }
      // Day-off preference required for part-time loads that offer a day choice
      // (0.5/0.6 = two days off, 0.75/0.8 = one day off). 0.4 has no day choice.
      const isTwoDayOff = availability.ftePreference === '0.5' || availability.ftePreference === '0.6';
      const isOneDayOff = availability.ftePreference === '0.75' || availability.ftePreference === '0.8';
      if ((isTwoDayOff || isOneDayOff) && !availability.dayOffPreference) {
        errs.push(
          isTwoDayOff
            ? 'Pick which two days you would prefer off (or "no preference" / "discuss")'
            : 'Pick which day you would prefer off (or "no preference" / "discuss")'
        );
      }
      if (preferences.curriculumAreas.length === 0) {
        errs.push('Select at least one learning area');
      }
      if (preferences.yearLevels.length === 0) {
        errs.push('Select at least one preferred year level');
      }
    }

    if (errs.length > 0) {
      setValidationErrors(errs);
      setError(null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setValidationErrors([]);

    try {
      setSaving(true);
      setError(null);

      // Derive intention to return value from employmentDetails
      const derivedIntentionToReturn = employmentDetails.employmentType === 'ongoing'
        ? employmentDetails.intentionToReturn || 'uncertain'
        : employmentDetails.wishToBeConsidered || 'uncertain';

      const intention: TeachingIntention = {
        id: existingIntention?.id || crypto.randomUUID(),
        staffId: staffIdentifier,
        staffName: user.name || '',
        email: user.email,
        department: user.department || '',
        year: currentPeriod.academicYear,
        periodId: currentPeriod.id,
        status: 'submitted',
        availability,
        preferences,
        employmentDetails,
        additionalComments: comments,
        submittedAt: new Date(),
        lastUpdatedAt: new Date(),
        // Map employmentDetails to legacy fields for backward compatibility
        currentEmploymentType: employmentDetails.employmentType === 'ongoing' ? 'full-time' : 'fixed-term',
        currentFTE: availability.fte,
        intentionToReturn: derivedIntentionToReturn,
        desiredEmploymentStatus: derivedIntentionToReturn === 'yes' ? 'full-time' : 'not-returning',
        targetFTE: availability.fte,
        teachingPreferences: [],
        comfortableTeachingAreas: preferences.curriculumAreas.map(a => a.areaName)
      };

      await intentionsService.saveIntentionForPeriod(intention, currentPeriod.id);

      setSuccessMessage('Your teaching intentions have been submitted successfully!');
      setExistingIntention({ ...intention, status: 'submitted' });
    } catch (err) {
      console.error('Error submitting intentions:', err);
      setError('Failed to submit intentions');
    } finally {
      setSaving(false);
    }
  };

  const getTimeRemaining = () => {
    if (!currentPeriod) return null;
    return intentionPeriodsService.getTimeUntilClose(currentPeriod);
  };

  const isSubmitted = existingIntention?.status === 'submitted' ||
                      existingIntention?.status === 'hr_approved' ||
                      existingIntention?.status === 'hr_flagged';

  // Phase 2: teachers can edit and re-submit at any time while the period is open.
  // Hard close is when period.status flips away from 'open' (handled by admin / cron at closesAt).
  const canEdit = !!currentPeriod && currentPeriod.status === 'open';

  // Late: set true only on the first submission if it arrives after the period's due_date.
  // The flag is surfaced to the teacher once set and never flipped back.
  const isLate = (existingIntention as any)?.isLate === true;

  // Mark the form dirty when the user edits it (skipping the initial data load).
  useEffect(() => {
    if (formLoadedRef.current) setDirty(true);
  }, [availability, preferences, employmentDetails, comments]);

  // The form is "loaded" one tick after the initial load settles, so load-time
  // state changes don't count as edits.
  useEffect(() => {
    if (!loading) {
      const t = setTimeout(() => { formLoadedRef.current = true; }, 0);
      return () => clearTimeout(t);
    }
  }, [loading]);

  // Debounced autosave-as-draft while the user is filling in the form (never once
  // submitted, so a submission is never quietly reverted to a draft). Silent on
  // error — the manual Save draft / Submit buttons still work.
  useEffect(() => {
    if (!dirty || !currentPeriod || !staffIdentifier || isSubmitted) return;
    const t = setTimeout(async () => {
      try {
        const intention = buildDraftIntention();
        await intentionsService.saveIntentionForPeriod(intention, currentPeriod.id);
        setExistingIntention(intention);
        setLastSavedAt(new Date());
        setDirty(false);
      } catch {
        /* silent — leave dirty so it retries and the user can still save manually */
      }
    }, 2500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, currentPeriod, staffIdentifier, isSubmitted]);

  // Warn before leaving with unsaved changes.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (dirty) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // Compute if the current in-progress answers would flag for HR leader discussion
  const hasDiscussFlag =
    availability.ftePreference === 'discuss' ||
    availability.dayOffPreference === 'discuss';

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-ink-200 rounded w-1/3"></div>
          <div className="h-64 bg-ink-200 rounded"></div>
        </div>
      </div>
    );
  }

  // No open period
  if (!currentPeriod) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-lg border border-ink-200 p-12 text-center">
          <Calendar className="w-16 h-16 text-ink-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-ink-900 mb-2">
            Teaching intentions are closed right now
          </h2>
          <p className="text-ink-500 max-w-md mx-auto">
            There's no open submission period at the moment, so you can't submit
            your teaching intentions yet. You'll be notified here when the next
            period opens.
          </p>
        </div>
      </div>
    );
  }

  // A period is open but we can't identify this staff member's record — show a
  // real, actionable error instead of pretending no period exists.
  if (!staffIdentifier) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-lg border border-warn-border p-12 text-center">
          <AlertTriangle className="w-16 h-16 text-warn-fg mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-ink-900 mb-2">
            We couldn't load your staff record
          </h2>
          <p className="text-ink-500 max-w-md mx-auto">
            A submission period is open, but your account isn't yet linked to a
            staff profile, so we can't attach your intentions to it. Please
            contact an administrator to have your staff record set up.
          </p>
        </div>
      </div>
    );
  }

  const timeRemaining = getTimeRemaining();

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="bg-white rounded-lg border border-ink-200 p-6 mb-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-ink-900">{currentPeriod.name}</h1>
            <p className="text-ink-500 mt-1">
              Submit your teaching intentions for the coming academic year
            </p>
          </div>

          {/* Status Badge */}
          {isSubmitted ? (
            <div className="flex items-center gap-2 px-4 py-2 bg-ok-bg text-ok-fg rounded-lg">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">Submitted</span>
            </div>
          ) : existingIntention?.status === 'draft' ? (
            <div className="flex items-center gap-2 px-4 py-2 bg-warn-bg text-warn-fg rounded-lg">
              <Clock className="w-5 h-5" />
              <span className="font-medium">Draft</span>
            </div>
          ) : null}
        </div>

        {/* Deadline Info */}
        {currentPeriod.dueDate && (
          <div className="mt-4 flex items-center gap-2 text-sm text-ink-500">
            <Clock className="w-4 h-4" />
            <span>
              Due by {new Date(currentPeriod.dueDate).toLocaleDateString()} — submissions received after this date are marked <strong>late</strong>.
            </span>
          </div>
        )}
        {timeRemaining && (
          <div className="mt-2 flex items-center gap-2 text-sm text-ink-500">
            <Clock className="w-4 h-4" />
            <span>
              Form closes in {timeRemaining.days} days, {timeRemaining.hours} hours
            </span>
          </div>
        )}

        {hasDiscussFlag && !isSubmitted && (
          <div className="mt-3 text-xs text-warn-fg bg-warn-bg border border-warn-border rounded px-3 py-2">
            Heads up: because you selected "I'd like to discuss my options", this submission will be flagged so someone can follow up with you personally.
          </div>
        )}

        {currentPeriod.description && (
          <div className="mt-4 p-4 bg-navy-50 rounded-lg text-sm text-navy-800">
            {currentPeriod.description}
          </div>
        )}
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 p-4 bg-ok-bg border border-ok-border rounded-lg text-ok-fg flex items-center gap-2">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          {successMessage}
        </div>
      )}

      {/* All outstanding validation problems, listed together */}
      {validationErrors.length > 0 && (
        <Alert
          tone="danger"
          title={validationErrors.length === 1 ? 'Please fix this before submitting' : `Please fix these ${validationErrors.length} things before submitting`}
          className="mb-6"
        >
          <ul className="list-disc list-inside space-y-0.5 mt-1">
            {validationErrors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </Alert>
      )}

      {isSubmitted && canEdit && (
        <div className="mb-6 p-4 bg-navy-50 border border-navy-200 rounded-lg text-navy-800">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">Your intentions have been submitted</p>
              <p className="text-sm mt-1">
                The submission period is still open — you can update your answers below and resubmit at any time until it closes.
              </p>
            </div>
          </div>
        </div>
      )}

      {isSubmitted && !canEdit && (
        <div className="mb-6 p-4 bg-ink-50 border border-ink-200 rounded-lg text-ink-700">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">Submission period is closed</p>
              <p className="text-sm mt-1">
                The submission period has closed. Contact HR if you need to make changes.
              </p>
            </div>
          </div>
        </div>
      )}

      {isLate && (
        <div className="mb-6 p-4 bg-warn-bg border border-warn-border rounded-lg text-warn-fg">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">Submitted late</p>
              <p className="text-sm mt-1">
                This submission was received after the due date. Your preferences are still recorded, but on-time submissions receive first priority during allocation.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Form Sections */}
      <div className="space-y-6">
        {/* Employment Details Section */}
        <EmploymentDetailsSection
          employmentDetails={employmentDetails}
          onChange={setEmploymentDetails}
          disabled={!canEdit || saving}
        />

        {/* Conditionally show remaining sections if returning/being considered or uncertain */}
        {(() => {
          const currentIntention = employmentDetails.employmentType === 'ongoing'
            ? employmentDetails.intentionToReturn
            : employmentDetails.wishToBeConsidered;
          // Show detailed sections for 'yes' AND 'uncertain' - we need data in case they return
          const showDetailedSections = currentIntention === 'yes' || currentIntention === 'uncertain';

          return (
            <>
              {/* Only show detailed sections if staff intends to return or is uncertain */}
              {showDetailedSections && (
                <>
                  {/* TIL Activities Section */}
                  <TILActivitiesSection
                    selectedActivities={employmentDetails.tilActivities}
                    onChange={(activities) => setEmploymentDetails({
                      ...employmentDetails,
                      tilActivities: activities
                    })}
                    disabled={!canEdit || saving}
                  />

                  {/* Availability Section */}
                  <AvailabilitySection
                    availability={availability}
                    onChange={setAvailability}
                    disabled={!canEdit || saving}
                  />

                  {/* Preferences Section */}
                  <PreferencesSection
                    preferences={preferences}
                    onChange={setPreferences}
                    academicYear={currentPeriod.academicYear}
                    disabled={!canEdit || saving}
                  />

                  {/* RUAH subjects — pulled live from RUAH grid for the period's year */}
                  <RUAHPreferencesSection
                    preferences={preferences}
                    onChange={setPreferences}
                    academicYear={currentPeriod.academicYear}
                    disabled={!canEdit || saving}
                  />
                </>
              )}
            </>
          );
        })()}

        {/* Comments Section */}
        <div className="bg-white rounded-lg border border-ink-200 p-6">
          <h3 className="text-lg font-semibold text-ink-900 mb-4">Additional Comments</h3>
          <textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            disabled={!canEdit || saving}
            rows={4}
            className="w-full px-3 py-2 border border-ink-300 rounded-md shadow-sm focus:ring-navy-500 focus:border-navy-500"
            placeholder="Any additional information you would like to share about your teaching intentions..."
          />
        </div>

        {/* Validation Error Message */}
        {error && (
          <div className="p-4 bg-danger-bg border border-danger-border rounded-lg text-danger-fg flex items-center gap-2">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Action Buttons */}
        {canEdit && (
          <div className="flex items-center justify-end gap-4">
            {!isSubmitted && (
              <span className="text-meta text-ink-400" aria-live="polite">
                {dirty ? 'Unsaved changes…' : lastSavedAt ? 'Draft autosaved' : ''}
              </span>
            )}
            <button
              onClick={handleSaveDraft}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 text-ink-700 bg-white border border-ink-300 rounded-lg hover:bg-ink-50 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              Save Draft
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2 bg-navy-600 text-white rounded-lg hover:bg-navy-700 disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
              {isSubmitted ? 'Update Submission' : 'Submit Intentions'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubmitIntentions;
